import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Input,
  InputSelect,
  Label,
  RadixTable,
  TColumn,
} from '@features/ui';
import Pagination from '@features/ui/components/pagination.tsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LeelooUser, UserReq } from '@src/types/users';
import { getBrowseUsers } from '@src/services/users.ts';
import { useCountryOptions } from '@src/hooks/useCountryOptions';
import { formatCurrency } from '@lib/utils.ts';

export const BrowseUsersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCountry = searchParams.get('country') as string | undefined;
  const [page, setPage] = useState(1);
  const [searchWords, setSearchWords] = useState('');
  const [totalPage, setTotalPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [users, setUsers] = useState<LeelooUser[]>([]);
  const [country, setCountry] = useState<string | undefined>(initialCountry);

  const navigate = useNavigate();
  const { countryOptions } = useCountryOptions();

  const fetchUsers = async (params?: UserReq) => {
    try {
      const res = await getBrowseUsers({
        ...params,
        country,
      });

      setUsers(res.data?.data ?? []);
      setTotalPage(res.data.total_pages);
      setTotalRecords(res.data.total || res.data.leeloo_users.length);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  useEffect(() => {
    const trimmed = searchWords.trim();
    const params: UserReq = trimmed
      ? { page, search_words: trimmed }
      : { page };

    fetchUsers(params).then();
  }, [page, country, searchWords]);

  useEffect(() => {
    if (!country) {
      setSearchParams({});
    }
  }, [country]);

  const handleSearch = useCallback(async () => {
    const trimmed = searchWords.trim();

    // If we're not on page 1, switch pages and let the effect perform the fetch
    if (page !== 1) {
      setPage(1);

      return;
    }
    // Already on page 1: fetch immediately so Enter triggers a search
    await fetchUsers(
      trimmed ? { page: 1, search_words: trimmed } : { page: 1 }
    );
  }, [page, searchWords]);

  const handleChangePage = useCallback(async (newPage: number) => {
    setPage(newPage);
  }, []);

  const handleViewUser = useCallback(
    (id?: number) => {
      if (!id) return;
      navigate(`/admin/browse-users/${id}`);
    },
    [navigate]
  );

  const columns = useMemo(
    (): TColumn<LeelooUser>[] => [
      {
        title: 'USERNAME',
        render: (item) => (
          <Button
            className={
              'text-[#3490FF] underline w-fit pl-0 text-xs font-normal'
            }
            variant={'link'}
            onClick={() => handleViewUser(item?.id)}
          >
            {item?.username || '-'}
          </Button>
        ),
      },
      {
        title: 'NAME',
        render: (item) => (
          <p className="text-sm">
            {item?.first_name && item?.last_name
              ? `${item.first_name} ${item.last_name}`
              : '-'}
          </p>
        ),
      },
      {
        title: 'E-MAIL ADDRESS',
        render: (item) => <p className="text-sm">{item?.email || '-'}</p>,
      },
      {
        title: 'PAYMENTS',
        render: (item) => {
          const paymentCount = Number(item?.payment ?? 0);

          if (!paymentCount) {
            return <p className="text-sm">Never</p>;
          }

          const totalPaymentAmountNumber = Number(
            (item as any)?.total_payment_amount ?? 0
          );

          return (
            <p className="text-sm">
              {paymentCount} - ${formatCurrency(totalPaymentAmountNumber, 2)}
            </p>
          );
        },
      },
    ],
    [handleViewUser]
  );

  const recordsInfo = useMemo(() => {
    const start = (page - 1) * 25 + 1;
    const end = Math.min(page * 25, totalRecords);

    return `${start}-${end}`;
  }, [page, totalRecords]);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Browse Users</h1>
        <p className="text-sm text-gray-400">
          showing records {recordsInfo} from {totalRecords.toLocaleString()}
        </p>
      </div>

      <Card className="p-6">
        <div className="mb-6">
          <Label className="text-sm mb-2 block">User Groups</Label>
          <div className="flex gap-4 items-end">
            <div className="flex max-w-lg gap-4 w-full">
              <Input
                placeholder="Login/Name/Email/Collected/IDs"
                textSize="sm"
                value={searchWords}
                onChange={(e) => setSearchWords(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
              <InputSelect
                allowClear
                borderColor={'border-[#979797]'}
                className={'rounded-[10px] text-sm min-w-[150px]'}
                options={countryOptions}
                placeholder="Country"
                value={country}
                onChange={(value) => {
                  setCountry(value);
                }}
              />
            </div>
          </div>
        </div>

        <RadixTable columns={columns} data={users} />

        {totalPage > 1 && (
          <div className="mt-6 flex justify-center">
            <Pagination
              current={page}
              total={totalPage}
              onChange={handleChangePage}
            />
          </div>
        )}
      </Card>
    </div>
  );
};
